/**
 * The Trailer reconciliation tab, sheet 20 callout 10 (mission 6 WP08 T046;
 * FR-014; ANA.5, ANA.4, U.4; mission 5 results-api.md "trailer").
 *
 * Each statistic the trailer declares against the count Trellis calculated,
 * in mono and in the API's order (sorted by name). A mismatch is an ERROR,
 * because the file is internally inconsistent, so it carries the ERROR badge
 * and the mismatch word; a match carries the consistency word. The same for
 * Path A and Path B. The list is short, so it is not paged. The not-ready
 * shape (`consistent: null`) is never fetched: the query waits for `ready`.
 * A trailer that declares no statistics says register 35's
 * `trailerNoStatistics` in place of an empty table (mission 8 WP10 T055;
 * FR-020 A-L3).
 */
import { CircleCheck } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import { SeverityBadge } from '@/components/trellis/SeverityBadge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableSkeletonRows } from '@/components/ui/table'
import { count } from '@/lib/format'
import { copyText } from '@/lib/provisional-copy'
import { trailerQuery } from '@/lib/queries'

import { ReadError } from './ReadError'

export function TrailerTable({ runId, ready }: { runId: string; ready: boolean }) {
  const query = useQuery(trailerQuery(runId, ready))
  if (query.isError) return <ReadError error={query.error} />
  if (query.data !== undefined && query.data.statistics.length === 0) {
    return <p className="text-body text-navy-700">{copyText('trailerNoStatistics')}</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Statistic</TableHead>
          <TableHead className="text-right">Declared</TableHead>
          <TableHead className="text-right">Calculated</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {query.data === undefined ? (
          <TableSkeletonRows columns={4} />
        ) : (
          query.data.statistics.map((s) => (
            <TableRow key={s.name}>
              <TableCell>{s.name}</TableCell>
              <TableCell numeric>{count(s.declared)}</TableCell>
              <TableCell numeric>{count(s.calculated)}</TableCell>
              <TableCell>
                {s.consistent ? (
                  <span className="inline-flex items-center gap-1.5 text-navy-700">
                    <CircleCheck aria-hidden className="size-4 text-[var(--color-success-700)]" />
                    {copyText('trailer.consistent')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <SeverityBadge severity="ERROR" />
                    {copyText('trailer.mismatch')}
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  )
}
